import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "@tanstack/react-router"
import { IconGauge2Fill18 } from "nucleo-ui-fill-18"
import { useDebounce } from "react-use"

import { AI_DEFAULT_OVERAGE_LIMIT_CENTS } from "@productlane/lib"
import { Tabs, TabsList, TabsTrigger } from "@productlane/ui"

import { AiTokenUsageChart } from "@/components/dashboard/ai-token-usage-chart"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { AiCreditUsageSummary } from "./ai-credit-usage-summary"
import { AI_LIMIT_FIELD_ID } from "./ai-limit-focus"
import { AiOverageLimitField } from "./ai-overage-limit-field"
import { useAddPaymentMethod } from "./use-add-payment-method"

const RANGES = [
	{ key: "month", label: "This month" },
	{ key: "3m", label: "3 months" },
	{ key: "12m", label: "12 months" },
] as const

type RangeKey = (typeof RANGES)[number]["key"]

// The credits cap email links here, since this card sits below the resolutions one.
const CARD_ID = "ai-credit-usage"

export function rangeStart(key: RangeKey, end: Date): Date {
	if (key === "month") {
		return new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1))
	}
	const days = key === "3m" ? 90 : 365
	return new Date(end.getTime() - days * 24 * 60 * 60 * 1000)
}

// Month view spans the whole calendar month so every day gets a column; future
// days render as empty gaps instead of stretching the past days into fat bars.
export function rangeEnd(key: RangeKey, now: Date): Date {
	if (key === "month") {
		return new Date(
			Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59),
		)
	}
	return now
}

export function AiTokenUsageSection() {
	const ctx = api.useUtils()
	const cardRef = useRef<HTMLDivElement>(null)
	const { hash } = useLocation()
	useEffect(() => {
		if (hash !== CARD_ID) return
		cardRef.current?.scrollIntoView({
			behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
				? "auto"
				: "smooth",
			block: "start",
		})
	}, [hash])
	const [rangeKey, setRangeKey] = useState<RangeKey>("month")
	// Freeze the range per rangeKey; a fresh `new Date()` each render would change
	// the query key every render and refetch forever.
	const { startDate, endDate } = useMemo(() => {
		const end = new Date()
		return {
			startDate: rangeStart(rangeKey, end),
			endDate: rangeEnd(rangeKey, end),
		}
	}, [rangeKey])

	const { data } = api.workspace.getAiTokenUsage.useQuery({
		startDate,
		endDate,
	})
	const updateCap = api.workspace.updateAiTokenCap.useMutation()
	const addPaymentMethod = useAddPaymentMethod()

	const [capEnabled, setCapEnabled] = useState(false)
	// The paused banner links straight to the field, so that arrival opens the
	// editor and the focus hook finds an input to select.
	const [capOpen, setCapOpen] = useState(hash === AI_LIMIT_FIELD_ID)
	const [capDollars, setCapDollars] = useState<string>("0")
	const [capInitialized, setCapInitialized] = useState(false)
	useEffect(() => {
		if (capInitialized || !data) return
		setCapInitialized(true)
		setCapEnabled(data.capUsd !== null)
		setCapDollars(data.capUsd === null ? "0" : String(data.capUsd))
	}, [data, capInitialized])

	const saveCap = (capCents: number | null) => {
		updateCap.mutate(
			{ capCents },
			{
				onSuccess: () => void ctx.workspace.getAiTokenUsage.invalidate(),
				onError: (error) => {
					captureError(error, {
						domain: "billing",
						action: "update ai token cap",
					})
					toast.error({ title: "Couldn't save your AI spend limit" })
				},
			},
		)
	}

	// Returns false on a value the field should reject, so the caller can explain why.
	const persistCap = (enabled: boolean, value: string) => {
		if (!enabled) {
			saveCap(null)
			return true
		}
		const trimmed = value.trim().replace(",", ".")
		const capCents = trimmed === "" ? 0 : Math.round(Number(trimmed) * 100)
		if (Number.isNaN(capCents) || capCents < 0) return false
		saveCap(capCents)
		return true
	}

	const toggleCap = (enabled: boolean) => {
		setCapEnabled(enabled)
		if (!enabled) {
			persistCap(false, capDollars)
			return
		}
		const next =
			capDollars.trim() === "" || Number(capDollars) === 0
				? String(AI_DEFAULT_OVERAGE_LIMIT_CENTS / 100)
				: capDollars
		setCapDollars(next)
		persistCap(true, next)
	}

	const commitCap = () => {
		if (persistCap(capEnabled, capDollars)) return
		toast.info({
			id: "ai-overage-limit-invalid",
			title: "Enter an overage limit of $0 or more",
			description: "Turn the limit off to allow unlimited overage.",
			icon: IconGauge2Fill18,
		})
	}

	useDebounce(
		() => {
			if (capInitialized) persistCap(capEnabled, capDollars)
		},
		700,
		[capDollars],
	)

	const spent = data ? `$${data.spentThisMonthUsd.toFixed(2)}` : "-"
	const allowance = data ? `$${data.freeAllowanceUsd.toFixed(2)}` : "-"
	const reachedLimit =
		!!data &&
		data.capUsd !== null &&
		data.spentThisMonthUsd > data.freeAllowanceUsd + data.capUsd

	return (
		<div id={CARD_ID} ref={cardRef} className="scroll-mt-4">
			<SettingsContainer className="mt-4">
				<SettingItem
					title="AI credit usage"
					description="AI credits your team used this month, by feature."
				/>

				<AiCreditUsageSummary
					spentUsd={data?.spentThisMonthUsd ?? 0}
					allowanceUsd={data?.freeAllowanceUsd ?? 0}
					capUsd={data?.capUsd ?? null}
				/>

				<AiOverageLimitField
					trialing={data?.trialing ?? false}
					reached={reachedLimit}
					enabled={capEnabled}
					value={capDollars}
					open={capOpen}
					onOpenChange={setCapOpen}
					onEnabledChange={toggleCap}
					onValueChange={setCapDollars}
					onCommit={commitCap}
					onAddPaymentMethod={() => void addPaymentMethod()}
				/>

				<div className="flex items-center justify-between pt-4">
					<div className="flex flex-col gap-0.5">
						<span className="text-sm text-tertiary">This month</span>
						<span className="text-2xl font-semibold tabular-nums text-primary">
							{spent}
						</span>
						<span className="text-xs tabular-nums text-tertiary">
							{allowance} credits included in your plan
						</span>
					</div>
					<Tabs
						value={rangeKey}
						onValueChange={(value) => setRangeKey(value as RangeKey)}
					>
						<TabsList>
							{RANGES.map((range) => (
								<TabsTrigger key={range.key} value={range.key}>
									{range.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>

				<AiTokenUsageChart startDate={startDate} endDate={endDate} />
			</SettingsContainer>
		</div>
	)
}
